import { useEffect, useMemo, useRef, useState } from "react";

import { useNavigate } from "react-router-dom";

import { useTranslation } from "react-i18next";

import { useQueryClient } from "@tanstack/react-query";

import PartDiagramModal, { DiagramMode } from "../../components/GtMotiveWidgets/PartDiagramModal";
import AllocatePartsToBinModal from "../../components/jobs/AllocatePartsToBinModal";
import JobDismantlingTimerCard from "../../components/jobs/JobDismantlingTimerCard";
import JobOverviewCard from "../../components/jobs/JobOverviewCard";
import JobPartsByAssembly from "../../components/jobs/JobPartsByAssembly";
import printDismantlingPlan, {
  buildJobAssemblyGroups,
  DismantlingPlanGroup,
  DismantlingPlanHeader,
} from "../../components/jobs/printDismantlingPlan";
import ScanPartActionModal from "../../components/jobs/ScanPartActionModal";
import ScanVehicleActionModal from "../../components/jobs/ScanVehicleActionModal";
import useNotification from "../../components/notifications/useNotifications";
import ActionMenu, { ActionMenuItem } from "../../components/shared/ActionMenu";
import Breadcrumbs from "../../components/shared/Breadcrumbs/Breadcrumbs";
import Card from "../../components/shared/Card/Card";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import ReasonedModal from "../../components/shared/Confirm/ReasonedModal";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import LoadingWheel from "../../components/shared/LoadingWheel";
import NotesOutput from "../../components/shared/Notes/NotesOutput";
import PinProtect, { PinProtectState } from "../../components/shared/PinProtect";
import ScanQrCode from "../../components/shared/ScanQrCode";
import VehicleLocationDropdown from "../../components/vehicles/VehicleLocationDropdown";
import { useAuth } from "../../contexts/AuthContext";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { GUID_REGEX, navigateForScan, useScannerOverride } from "../../contexts/ScannerContext";
import { readHiddenAssemblyIds } from "../../components/jobs/JobPartsByAssembly";
import useFlowTypeAccounts from "../../data/useFlowTypeAccounts";
import useJobAssemblySession from "../../data/useJobAssemblySession";
import useNotes from "../../data/useNotes";
import usePartAssembly from "../../data/usePartAssembly";
import usePartBins from "../../data/usePartBins";
import usePartMutations from "../../data/usePartMutations";
import useSettings from "../../data/useSettings";
import useSingleJob from "../../data/useSingleJob";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { useLabelGenerator } from "../../hooks/useLabelGenerator";
import { IJob, JobPartCategory } from "../../models/Job";
import { INewNote, ResourceType } from "../../models/Note";
import { IPart, IPartAssembly, IPartCategory, PartStatus } from "../../models/Part";
import { AccountType } from "../../models/account/IAccount";
import classNames from "../../utils/classNames";
import { parseScanAction, ScanAction } from "../../utils/scanActions";
import { parseVehicleScan, scanMatchesVehicle } from "../../utils/vehicleScan";
import Depollution from "../Depollution/Depollution";
import Triage from "../Triage/Triage";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import useStockPermissions from "../../data/useStockPermissions";
import useSingleSetting from "../../data/useSingleSetting";
import printHtml from "../../utils/printHtml";

export interface JobWorkspaceProps {
  /** The job to display and manage. */
  jobId: string;
  /**
   * When set, the parts list is restricted to the single assembly with this id
   * (used by Station Manager). The overview cards still show the whole vehicle.
   */
  restrictToAssemblyId?: string;
  /**
   * When true (default) this component owns the page title/description via
   * PageTitleContext. Set to false when a host page (e.g. Station Manager)
   * wants to keep its own title.
   */
  managePageTitle?: boolean;
  /**
   * When provided, scanning a vehicle that doesn't belong to this job prompts
   * the user to open that vehicle here instead of showing a "wrong vehicle"
   * warning. Used by Station Manager so a worker can move from one vehicle to
   * the next without leaving the station. The third argument is true when the
   * switch should also start the station's timer and scroll to the parts.
   */
  onSwitchVehicle?: (vehicleId: string, vrm: string | undefined, startTimer: boolean) => void;
  /**
   * When true, the station's assembly timer is started automatically once the
   * job has loaded (if no timer is already running) and the view scrolls down
   * to the parts list. Used by Station Manager after a re-scan switch.
   */
  autoStartTimer?: boolean;
  /**
   * The part whose label was scanned to open this job in Station Manager.
   * Re-scanning that same label drives the assembly timers exactly like
   * re-scanning the job's vehicle label, so a worker who entered the station
   * with a part tag can also finish with it.
   */
  timerTogglePartId?: string;
  /** Hide the Triage / Depollution section (Station Manager). */
  hideTriage?: boolean;
  /** Render the dismantling timer collapsed by default (Station Manager). */
  collapseDismantlingTimer?: boolean;
  /** Hide the overall dismantling timer card entirely (Station Manager). */
  hideDismantlingTimer?: boolean;
  /** Hide the job overview card (Station Manager). */
  hideOverview?: boolean;
  /**
   * Hide parts assigned to a Recycling account from the parts grid (Station
   * Manager). They stay on the job — timers and the scan "mark the rest as
   * removed" flow still include them — and are viewable via a per-assembly
   * list icon.
   */
  hideRecyclingParts?: boolean;
  /**
   * Called after an assembly timer finishes successfully. Used by Station
   * Manager to clear the loaded vehicle and return to the ready-to-scan UI.
   */
  onSessionFinished?: () => void;
  /**
   * Station Manager's station / vehicle picker. When provided the workspace
   * renders a compact two-column header - the picker with the job toolbar
   * beneath it on the left, job notes and the vehicle QR merged on the right -
   * and drops the breadcrumb and the standalone toolbars.
   */
  stationPicker?: React.ReactNode;
  /**
   * Station Manager's dense parts layout: single-row assembly headers with the
   * timer inline and compact part cards (account, actions and QR only) that
   * fit four across when the sidebar is collapsed.
   */
  compactParts?: boolean;
}

export default function JobWorkspace({
  jobId,
  restrictToAssemblyId,
  managePageTitle = true,
  onSwitchVehicle,
  autoStartTimer = false,
  timerTogglePartId,
  hideTriage = false,
  collapseDismantlingTimer = false,
  hideDismantlingTimer = false,
  hideOverview = false,
  hideRecyclingParts = false,
  onSessionFinished,
  stationPicker,
  compactParts = false,
}: JobWorkspaceProps) {
  const { t } = useTranslation("jobs");
  const { setInfo } = usePageNameContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addNotification } = useNotification();

  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers(t("Job"));
  const { saveHandlers: partSaveHandlers } = useDefaultCRUDHandlers(t("Part"));
  const { userId } = useAuth();
  const { restricted } = useStockPermissions();
  const { create, remove } = useNotes();
  const [showDeleted, setShowDeleted] = useState(false);
  const { singleJob, update, remove: removeJob } = useSingleJob(jobId!, showDeleted);
  const { markDamaged, patchPart, bulkPatchPart } = usePartMutations();
  const { assemblies } = usePartAssembly();
  const { partBins } = usePartBins();
  const { sessions: assemblySessions, startSession, pauseSession, finishSession } = useJobAssemblySession(jobId);
  const { generatePartLabel, generateVehicleLabel } = useLabelGenerator();
  const { flowTypeAccounts } = useFlowTypeAccounts("JobWorkspace");
  const { singleSetting } = useSingleSetting("IgnorePartPin");
  const hasTriage = CheckFeatureFlag("Triage");
  const hasTmeFeature = CheckFeatureFlag("TME");
  const hasLeads = CheckFeatureFlag("Lead Management");

  // When the IgnorePartPin setting is enabled we skip the PIN modal entirely
  // and act as the currently logged-in user.
  const ignorePartPin = useMemo(() => {
    return singleSetting.isLoading ? false : singleSetting.data?.data?.value === "true";
  }, [singleSetting.isLoading, singleSetting.data?.data?.value]);

  const [job, setJob] = useState<IJob | undefined>();
  const [completeOpen, setCompleteOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [damagedOpen, setDamagedOpen] = useState(false);
  const [damaged, setDamaged] = useState<IPart | null>();
  const [missingOpen, setMissingOpen] = useState(false);
  const [missing, setMissing] = useState<IPart | null>();
  const [isCompleteLoading, setIsCompleteLoading] = useState(false);
  const [isDamagedLoading, setIsDamagedLoading] = useState(false);
  const [isMissingLoading, setIsMissingLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);


  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [pinState, setPinState] = useState<PinProtectState>();
  const [selectedPartIds, setSelectedPartIds] = useState<Set<string>>(new Set());

  // The part a scan is currently asking the user to act on. Scanning the same
  // part again confirms removal; see the scanner override below.
  const [scanConfirmPart, setScanConfirmPart] = useState<IPart | null>(null);

  // The part a job note is being written against from the scan modal's
  // "Add note" action (the one step in that flow that needs the keyboard).
  const [noteForPart, setNoteForPart] = useState<IPart | null>(null);
  const [isNoteLoading, setIsNoteLoading] = useState(false);

  // The assembly whose timer is running when the vehicle is re-scanned with
  // outstanding parts. Drives the bulk-action modal; re-scanning while it is
  // open finishes the timer (see toggleNextAssemblyTimer).
  const [scanVehicleAssembly, setScanVehicleAssembly] = useState<IPartAssembly | null>(null);

  // The allocate-parts-to-bin flow. Opened from the Actions menu or by scanning a bin label;
  // while it's open, part scans tick parts and re-scanning the bin commits the allocation.
  const [allocateOpen, setAllocateOpen] = useState(false);
  const [allocateBinId, setAllocateBinId] = useState<string | null>(null);
  const [allocatePartIds, setAllocatePartIds] = useState<Set<string>>(new Set());

  // A different vehicle was scanned while this job is open in Station Manager.
  // Drives the "open that vehicle?" prompt; re-scanning while it is open opens
  // the vehicle and starts its timer (see the scanner override below).
  const [pendingSwitchVehicle, setPendingSwitchVehicle] = useState<{ id: string; vrm?: string } | null>(null);

  // Parts list, scrolled into view on an auto-start switch.
  const partsSectionRef = useRef<HTMLDivElement | null>(null);
  // Guards the one-shot auto-start so it only fires once per loaded job.
  const autoStartedRef = useRef(false);

  const [diagramOpen, setDiagramOpen] = useState(false);
  const [diagramCategory, setDiagramCategory] = useState<IPartCategory | null>(null);
  const [diagramMode, setDiagramMode] = useState<DiagramMode>({ type: "findAll" });
  const [diagramPartDataId, setDiagramPartDataId] = useState<string | null>(null);


  useEffect(() => {
    if (!singleJob.isLoading) {
      setJob(singleJob.data?.data);

      if (managePageTitle) {
        setInfo({
          name: t("{{title}} Job", { title: singleJob.data?.data.title }),
          desc: t("View and manage {{title}}'s Job", { title: singleJob.data?.data.title }),
        });
      }

      setIsLoading(false);
    }
  }, [singleJob.isLoading, singleJob.data, jobId, managePageTitle])

  // Opens the PIN modal for a protected action, unless the IgnorePartPin
  // setting is on — in which case we run the success callback straight away
  // as the current user.
  const requestPin = (state: PinProtectState) => {
    if (ignorePartPin && userId) {
      state.onPinSuccess(userId);
      return;
    }
    setPinModalOpen(true);
    setPinState(state);
  };

  const willAssemblyBeCompleteAfterPatching = (
    assembly: IPartAssembly,
    patchedPartIds: Set<string>
  ): boolean => {
    const categoryIds = new Set(
      (assembly.partAssemblyPartCategories ?? [])
        .map(c => c.partCategoryId)
        .filter((id): id is string => !!id)
    );
    const assemblyJobParts = (job?.jobPartCategories ?? []).filter(
      jpc => jpc.partCategoryId && categoryIds.has(jpc.partCategoryId)
    );
    if (assemblyJobParts.length === 0) return false;
    return assemblyJobParts.every(jpc => {
      const p = jpc.partData;
      if (!p) return true;
      if (patchedPartIds.has(p.id)) return true;
      return !!p.removedOn || !!p.isDamaged || !!p.isMissing || !!p.isNotAccessible;
    });
  };

  const finishAssemblySessionIfActive = (partAssemblyId: string, pinUserId: string | null) => {
    if (!jobId || !pinUserId) return;
    const activeSession = assemblySessions.data?.data.find(
      s => s.partAssemblyId === partAssemblyId && !s.dismantlingEnd
    );
    if (!activeSession) return;
    finishSession.mutate(
      { jobId, partAssemblyId, userId: pinUserId },
      { onSuccess: () => onSessionFinished?.() }
    );
  };

  const getAssemblyById = (partAssemblyId: string | null | undefined): IPartAssembly | undefined =>
    partAssemblyId ? assemblies.data?.data?.find(a => a.id === partAssemblyId) : undefined;

  // The parts in an assembly that are still outstanding (not removed, not
  // damaged and not deleted) — i.e. what a "remove/damage the rest" action acts on.
  const outstandingPartsForAssembly = (assembly: IPartAssembly | undefined): IPart[] => {
    if (!assembly || !job) return [];
    const categoryIds = new Set(
      (assembly.partAssemblyPartCategories ?? [])
        .map(c => c.partCategoryId)
        .filter((id): id is string => !!id)
    );
    return job.jobPartCategories
      .filter(jpc => jpc.partCategoryId && categoryIds.has(jpc.partCategoryId))
      .map(jpc => jpc.partData)
      .filter((p): p is IPart => {
        if (!p || p.removedOn || p.isDamaged || p.isMissing || p.isNotAccessible) return false;
        return !(p as IPart & { deleted?: string | null }).deleted;
      });
  };

  // Mark every outstanding part in the assembly as removed under a single PIN.
  // When the last part lands the assembly is complete and its timer auto-finishes.
  const markAllRemovedFromScan = (assembly: IPartAssembly, parts: IPart[]) => {
    if (!jobId || parts.length === 0) return;
    requestPin({
      onPinSuccess: (pinUserId) => {
        const patchedPartIds = new Set(parts.map(p => p.id));
        let finished = false;
        parts.forEach(part => {
          patchPart.mutate(
            {
              id: part.id,
              removedOn: new Date().toISOString(),
              removedBy: pinUserId,
              partStatus: PartStatus.Dismantled,
            },
            {
              onSuccess: () => {
                partSaveHandlers.onSuccess();
                if (!finished && willAssemblyBeCompleteAfterPatching(assembly, patchedPartIds)) {
                  finished = true;
                  finishAssemblySessionIfActive(assembly.id, pinUserId);
                }
              },
              onError: partSaveHandlers.onError,
            }
          );
        });
      },
      onPinFailed: () => { },
      pinLabel: parts.length > 1
        ? t("Mark {{count}} Parts as Removed", { count: parts.length })
        : t("Mark {{count}} Part as Removed", { count: parts.length }),
    });
  };

  const pauseTimerFromScan = (assembly: IPartAssembly) => {
    if (!jobId) return;
    requestPin({
      onPinSuccess: (pinUserId) => {
        if (!pinUserId) return;
        pauseSession.mutate(
          { jobId, partAssemblyId: assembly.id, userId: pinUserId, pauseReason: "Paused" },
          {
            onSuccess: () => addNotification({ variant: "success", primaryText: t("Paused timer: {{name}}", { name: assembly.name }) }),
            onError: () => addNotification({ variant: "danger", primaryText: t("Unable to pause {{name}}", { name: assembly.name }) }),
          }
        );
      },
      onPinFailed: () => { },
      pinLabel: t("Pause {{name}}", { name: assembly.name }),
    });
  };

  const finishTimerFromScan = (assembly: IPartAssembly) => {
    if (!jobId) return;
    requestPin({
      onPinSuccess: (pinUserId) => {
        if (!pinUserId) return;
        finishSession.mutate(
          { jobId, partAssemblyId: assembly.id, userId: pinUserId },
          {
            onSuccess: () => {
              addNotification({ variant: "success", primaryText: t("Finished timer: {{name}}", { name: assembly.name }) });
              onSessionFinished?.();
            },
            onError: () => addNotification({ variant: "danger", primaryText: t("Unable to finish {{name}}", { name: assembly.name }) }),
          }
        );
      },
      onPinFailed: () => { },
      pinLabel: t("Finish {{name}}", { name: assembly.name }),
    });
  };

  // Finish Timer in the action modal, or a re-scan while it's open: bulk-mark outstanding
  // parts as removed (which auto-finishes the timer), or just finish if none are left.
  const finishFromScanModal = () => {
    const assembly = scanVehicleAssembly;
    if (!assembly) return;
    const outstanding = outstandingPartsForAssembly(assembly);
    setScanVehicleAssembly(null);
    if (outstanding.length > 0) {
      markAllRemovedFromScan(assembly, outstanding);
    } else {
      finishTimerFromScan(assembly);
    }
  };

  // TME: the Finish button gets the same "parts still outstanding" confirmation a scan does, so
  // an operator finishing from the button can pause instead or confirm the remaining parts as
  // removed. With nothing outstanding the button finishes as normal.
  const confirmFinishWithOutstanding = (assembly: IPartAssembly): boolean => {
    if (outstandingPartsForAssembly(assembly).length === 0) return false;
    setScanVehicleAssembly(assembly);
    return true;
  };

  // The first assembly (in display order) that still has outstanding parts,
  // skipping assemblies the user has hidden. Mirrors JobPartsByAssembly's
  // ordering (assemblies.data.data order) and "completed" definition (every
  // part removed or damaged), including assemblies with no parts. When the
  // workspace is restricted to a single assembly (Station Manager) only that
  // assembly is considered.
  const findNextIncompleteAssembly = (): IPartAssembly | undefined => {
    if (!job || !assemblies.data?.data) return undefined;

    const hiddenIds = readHiddenAssemblyIds();
    const jobParts = job.jobPartCategories.filter((jpc) => {
      const part = jpc.partData as (IPart & { deleted?: string | null }) | null | undefined;
      return showDeleted || !part?.deleted;
    });

    return assemblies.data.data.find((assembly) => {
      if (restrictToAssemblyId && assembly.id !== restrictToAssemblyId) return false;
      if (!restrictToAssemblyId && hiddenIds.includes(assembly.id)) return false;

      const categoryIds = new Set(
        (assembly.partAssemblyPartCategories ?? [])
          .map((c) => c.partCategoryId)
          .filter((id): id is string => !!id)
      );
      const assemblyParts = jobParts.filter(
        (jpc) => jpc.partCategoryId && categoryIds.has(jpc.partCategoryId)
      );
      // An assembly with no parts on this job is still worth timing - the
      // operator is stood at it either way - and it's shown in the parts list
      // with its timer, so it's a valid target.
      if (assemblyParts.length === 0) return true;

      const completed = assemblyParts.every(
        (jpc) =>
          !!jpc.partData?.removedOn ||
          !!jpc.partData?.isDamaged ||
          !!jpc.partData?.isMissing ||
          !!jpc.partData?.isNotAccessible
      );
      return !completed;
    });
  };

  // Scanning the job's vehicle drives the assembly timers:
  //   - no timer running        -> start the next incomplete assembly
  //   - timer running + parts    -> open the bulk-action modal
  //   - timer running, no parts  -> just finish
  //   - modal already open       -> mark remaining parts removed and finish
  const toggleNextAssemblyTimer = () => {
    if (!jobId) return;

    if (scanVehicleAssembly) {
      finishFromScanModal();
      return;
    }

    const activeSession = assemblySessions.data?.data.find((s) => !s.dismantlingEnd);

    if (activeSession?.partAssemblyId) {
      const assembly = getAssemblyById(activeSession.partAssemblyId);
      if (!assembly) return;

      const outstanding = outstandingPartsForAssembly(assembly);
      if (outstanding.length > 0) {
        // Timer running with parts still to clear -> offer the bulk actions.
        setScanVehicleAssembly(assembly);
      } else {
        // Nothing left to mark -> finish straight away.
        finishTimerFromScan(assembly);
      }
      return;
    }

    // No timer running -> start the next incomplete assembly.
    const target = findNextIncompleteAssembly();
    if (!target) {
      addNotification({
        variant: "warning",
        primaryText: t("No assembly to time"),
        secondaryText: t("Every visible assembly is already completed."),
      });
      return;
    }

    startSession.mutate(
      { jobId, partAssemblyId: target.id },
      {
        onSuccess: () => {
          addNotification({ variant: "success", primaryText: t("Started timer: {{name}}", { name: target.name }) });
          autoPrintStationLabels();
        },
        onError: () => addNotification({ variant: "danger", primaryText: t("Unable to start {{name}}", { name: target.name }) }),
      }
    );
  };

  // Start the station's timer if nothing is already running, then scroll the
  // parts list into view. Used by the auto-start switch in Station Manager.
  const startStationTimerIfIdle = () => {
    if (!jobId) return;
    const activeSession = assemblySessions.data?.data.find((s) => !s.dismantlingEnd);
    if (!activeSession) {
      const target = findNextIncompleteAssembly();
      if (target) {
        startSession.mutate(
          { jobId, partAssemblyId: target.id },
          {
            onSuccess: () => {
              addNotification({ variant: "success", primaryText: t("Started timer: {{name}}", { name: target.name }) });
              autoPrintStationLabels();
            },
            onError: () => addNotification({ variant: "danger", primaryText: t("Unable to start {{name}}", { name: target.name }) }),
          }
        );
      }
    }
    setTimeout(() => {
      partsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  // When Station Manager opens a vehicle via a re-scan, auto-start its timer and
  // scroll to the parts once the job and its sessions have loaded.
  useEffect(() => {
    if (!autoStartTimer || autoStartedRef.current) return;
    if (isLoading || !job) return;
    if (assemblies.isLoading || assemblySessions.isLoading) return;
    autoStartedRef.current = true;
    startStationTimerIfIdle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStartTimer, isLoading, job, assemblies.isLoading, assemblySessions.isLoading]);

  // The job's parts that can be put in a bin - everything the parts list shows, in the same
  // order, minus deleted parts. In Station Manager that's this station's assembly only, and
  // recycling parts are left out on the same basis they're kept off the grid: they don't get
  // picked into a bin, they go to the recycling flow.
  const allocatableParts = useMemo<IPart[]>(() => {
    let scoped = job?.jobPartCategories ?? [];

    if (restrictToAssemblyId) {
      const assembly = assemblies.data?.data?.find((a) => a.id === restrictToAssemblyId);
      const categoryIds = new Set(
        (assembly?.partAssemblyPartCategories ?? [])
          .map((c) => c.partCategoryId)
          .filter((id): id is string => !!id)
      );
      scoped = scoped.filter((jpc) => jpc.partCategoryId && categoryIds.has(jpc.partCategoryId));
    }

    if (hideRecyclingParts) {
      const recyclingAccountIds = new Set(
        (flowTypeAccounts.data?.data ?? [])
          .filter((a) => a.accountType === AccountType.Recycling)
          .map((a) => a.id)
      );
      scoped = scoped.filter((jpc) => {
        const accountId = jpc.partData?.accountId;
        return !accountId || !recyclingAccountIds.has(accountId);
      });
    }

    return scoped
      .map((jpc) => jpc.partData)
      .filter((p): p is IPart => {
        if (!p) return false;
        return !(p as IPart & { deleted?: string | null }).deleted;
      });
  }, [job, assemblies.data, restrictToAssemblyId, hideRecyclingParts, flowTypeAccounts.data]);

  const openAllocateModal = (binId: string | null, seedSelection: boolean) => {
    // Don't leave another scan prompt sat underneath this one.
    setScanConfirmPart(null);
    setScanVehicleAssembly(null);
    setAllocateBinId(binId);
    setAllocatePartIds(
      seedSelection
        ? new Set([...selectedPartIds].filter((id) => allocatableParts.some((p) => p.id === id)))
        : new Set()
    );
    setAllocateOpen(true);
  };

  const toggleAllocatePart = (partId: string) => {
    setAllocatePartIds((prev) => {
      const next = new Set(prev);
      if (next.has(partId)) next.delete(partId);
      else next.add(partId);
      return next;
    });
  };

  const toggleAllAllocateParts = () => {
    setAllocatePartIds((prev) =>
      prev.size === allocatableParts.length ? new Set() : new Set(allocatableParts.map((p) => p.id))
    );
  };

  // Move the ticked parts into the bin in one bulk patch. Bins are a logistics record rather than a
  // change to the part's dismantling state, so unlike remove/damage this isn't PIN protected.
  const allocatePartsToBin = (binId: string, partIds: string[]) => {
    if (!binId || partIds.length === 0) return;

    const binNameForToast = partBins.data?.data?.find((b) => b.id === binId)?.name ?? t("bin");

    bulkPatchPart.mutate(
      { partIds, data: { partBinId: binId, updatePartBin: true } },
      {
        onSuccess: () => {
          addNotification({
            variant: "success",
            primaryText:
              partIds.length > 1
                ? t("Allocated {{count}} parts to {{name}}", { count: partIds.length, name: binNameForToast })
                : t("Allocated {{count}} part to {{name}}", { count: partIds.length, name: binNameForToast }),
          });
          setAllocateOpen(false);
          setAllocateBinId(null);
          setAllocatePartIds(new Set());
        },
        onError: () =>
          addNotification({
            variant: "danger",
            primaryText: t("Unable to allocate parts to {{name}}", { name: binNameForToast }),
          }),
      }
    );
  };

  const handleAllocateConfirm = () => {
    if (!allocateBinId) {
      addNotification({ variant: "warning", primaryText: t("Pick a bin first") });
      return;
    }
    if (allocatePartIds.size === 0) {
      addNotification({ variant: "warning", primaryText: t("No Parts Selected") });
      return;
    }
    allocatePartsToBin(allocateBinId, [...allocatePartIds]);
  };

  // A bin label was scanned. Opens the allocate flow on that bin, switches to it if a different
  // bin is scanned mid-flow, or - when it's the bin already selected - commits the allocation.
  const handleBinScan = (scannedBinId: string, scannedBinName?: string) => {
    const bin = partBins.data?.data?.find((b) => b.id.toLowerCase() === scannedBinId.toLowerCase());

    // Only reject the scan once the bin list has actually loaded - otherwise trust the label.
    if (!bin && !partBins.isLoading) {
      addNotification({
        variant: "warning",
        primaryText: t("Bin not found"),
        secondaryText: t("{{name}} isn't a bin on this system any more.", {
          name: scannedBinName ?? t("That bin"),
        }),
      });
      return;
    }

    const binId = bin?.id ?? scannedBinId;

    if (allocateOpen && allocateBinId && binId.toLowerCase() === allocateBinId.toLowerCase()) {
      // Re-scanning the selected bin is the "done" signal.
      if (allocatePartIds.size === 0) {
        addNotification({
          variant: "warning",
          primaryText: t("No Parts Selected"),
          secondaryText: t("Scan the parts going into this bin first."),
        });
        return;
      }
      allocatePartsToBin(binId, [...allocatePartIds]);
      return;
    }

    if (allocateOpen) {
      // A different bin mid-flow -> allocate into that one instead, keeping the ticked parts.
      setAllocateBinId(binId);
      addNotification({
        variant: "success",
        primaryText: t("Allocating to {{name}}", { name: bin?.name ?? scannedBinName ?? t("bin") }),
      });
      return;
    }

    openAllocateModal(binId, false);
  };

  // A static action code ("a|{action}") acts on the part whose scan opened the
  // part action modal, so the operator can drive the whole flow from the
  // scanner. Damaged goes straight through with no reason typed - the "note"
  // action is there for when one is actually needed. Missing asks for one.
  const handleActionScan = (action: ScanAction) => {
    const part = scanConfirmPart;
    if (!part) {
      addNotification({
        variant: "warning",
        primaryText: t("Scan a part first"),
        secondaryText: t("Action codes work on the part you've just scanned."),
      });
      return;
    }
    setScanConfirmPart(null);
    switch (action) {
      case "removed":
        markAsRemoved(part);
        break;
      case "damaged":
        submitDamaged(part, "");
        break;
      case "missing":
        // Missing needs a reason - mechanics were using it for parts they'd broken - so the scan
        // opens the same dialog as the button rather than going straight through.
        setMissing(part);
        setMissingOpen(true);
        break;
      case "notaccessible":
        submitNotAccessible(part);
        break;
      case "note":
        setNoteForPart(part);
        break;
      case "cancel":
        break;
    }
  };

  useScannerOverride((scan) => {
    const { parts } = scan;

    // Action codes -> act on the pending scanned part. "a|{action}".
    const action = parseScanAction(parts);
    if (action) {
      handleActionScan(action);
      return;
    }

    // Bin scans -> the allocate-parts-to-bin flow. "bin|{binId}|{binName}".
    if (parts[0]?.toLowerCase() === "bin") {
      const scannedBinId = parts[1];
      if (scannedBinId) handleBinScan(scannedBinId, parts[2]);
      return;
    }

    // Vehicle scans are handled here rather than navigating away.
    if (parts[0]?.toLowerCase() === "v") {
      // The allocate flow owns the scanner while it's open, so a vehicle scan doesn't drop a
      // timer prompt on top of it.
      if (allocateOpen) {
        addNotification({
          variant: "warning",
          primaryText: t("Finish allocating first"),
          secondaryText: t("Scan the bin again to allocate, or close the window."),
        });
        return;
      }

      const scannedVehicleId = parts[1];
      const scannedVrm = parts[2];
      const scannedVin = parseVehicleScan(parts)?.vin;

      // A second scan while the "open that vehicle?" prompt is up opens the
      // vehicle in this station, starts the timer and scrolls down.
      if (pendingSwitchVehicle && scannedVehicleId && onSwitchVehicle) {
        setPendingSwitchVehicle(null);
        onSwitchVehicle(scannedVehicleId, scannedVrm, true);
        return;
      }

      // Id first, reg as a fallback - a tag for this reg can carry a different
      // id (see scanMatchesVehicle) and must still count as our vehicle.
      if (scanMatchesVehicle({ id: scannedVehicleId, vrm: scannedVrm, vin: scannedVin }, job?.lead?.vehicle)) {
        // Our vehicle -> drive the assembly timers.
        toggleNextAssemblyTimer();
      } else if (scannedVehicleId && onSwitchVehicle) {
        // A different vehicle in Station Manager -> offer to open it here.
        setPendingSwitchVehicle({ id: scannedVehicleId, vrm: scannedVrm });
      } else {
        // A different vehicle -> warn instead of redirecting.
        addNotification({
          variant: "warning",
          primaryText: t("Wrong vehicle"),
          secondaryText: t("That vehicle doesn't match this job ({{vrm}}).", { vrm: job?.lead?.vehicle?.vrm ?? t("this job's vehicle") }),
        });
      }
      return;
    }

    // Part scans -> if the part is on this job, prompt to remove it.
    // "p|{partId}|{tag}" or, temporarily, a bare GUID with no prefix.
    const isBareGuid = parts.length === 1 && GUID_REGEX.test(parts[0]);
    if (parts[0]?.toLowerCase() === "p" || isBareGuid) {
      const scannedPartId = isBareGuid ? parts[0] : parts[1];

      // While the allocate flow is open, part scans tick parts for the bin rather than driving
      // the remove/damage flow. Scanning the same part again unticks it.
      if (allocateOpen) {
        const part = scannedPartId
          ? allocatableParts.find((p) => p.id.toLowerCase() === scannedPartId.toLowerCase())
          : undefined;

        if (!part) {
          // In Station Manager the list is this station's assembly minus recycling parts, so a
          // part can be on the job and still not be one of ours - say which it is rather than
          // claiming it isn't on the job at all.
          const onJob = scannedPartId
            ? job?.jobPartCategories.some(
              (jpc) => jpc.partData?.id?.toLowerCase() === scannedPartId.toLowerCase()
            )
            : false;

          addNotification({
            variant: "warning",
            primaryText: onJob ? t("Part not at this station") : t("Part not on this job"),
            secondaryText: onJob
              ? t("That part isn't one of this station's parts.")
              : t("Scan a part that belongs to this job to put it in the bin."),
          });
          return;
        }

        const wasSelected = allocatePartIds.has(part.id);
        toggleAllocatePart(part.id);
        addNotification({
          variant: wasSelected ? "warning" : "success",
          primaryText: wasSelected
            ? t("Removed {{title}}", { title: part.title ?? t("part") })
            : t("Added {{title}}", { title: part.title ?? t("part") }),
        });
        return;
      }

      // The label that opened this station acts like the vehicle label: a
      // re-scan drives the assembly timers (start / bulk-finish) instead of
      // the part remove/damage flow.
      if (
        scannedPartId &&
        timerTogglePartId &&
        scannedPartId.toLowerCase() === timerTogglePartId.toLowerCase()
      ) {
        toggleNextAssemblyTimer();
        return;
      }

      const jobPart = scannedPartId
        ? job?.jobPartCategories.find(
          (jpc) => jpc.partData?.id?.toLowerCase() === scannedPartId.toLowerCase()
        )
        : undefined;

      if (!jobPart?.partData) {
        addNotification({
          variant: "warning",
          primaryText: t("Part not on this job"),
          secondaryText: t("Scan a part that belongs to this job to remove it."),
        });
      } else if (jobPart.partData.removedOn) {
        addNotification({
          variant: "warning",
          primaryText: t("Part already removed"),
          secondaryText: t("{{title}} is already marked as removed.", { title: jobPart.partData.title ?? t("This part") }),
        });
      } else if (scanConfirmPart && scanConfirmPart.id === jobPart.partData.id) {
        // Same part scanned again -> confirm removal.
        setScanConfirmPart(null);
        markAsRemoved(jobPart.partData);
      } else {
        // First scan of this part -> offer Remove / Damage, or re-scan to remove.
        setScanConfirmPart(jobPart.partData);
      }
      return;
    }

  });

  const handleComplete = () => {
    setIsCompleteLoading(true);

    if (!userId || !job) {
      saveHandlers.onError();
      return;
    }

    const updatedJob = {
      ...job!,
      completedOn: new Date().toISOString(),
      completedBy: userId,
    }

    update.mutate(updatedJob, {
      onSuccess: () => {
        saveHandlers.onSuccess();
        setCompleteOpen(false);
      },
      onError: saveHandlers.onError,
      onSettled: () => setIsCompleteLoading(false),
    });

  }

  const handleReset = () => {
    if (!userId || !job) {
      saveHandlers.onError();
      return;
    }

    const updatedJob: IJob = {
      ...job!,
      startDate: null,
      endDate: null,
      mechanicId: null,
      completedBy: null,
      completedOn: null,
    }

    update.mutate(updatedJob, saveHandlers);
  }

  const handleReopen = () => {
    if (!userId || !job) {
      saveHandlers.onError();
      return;
    }

    const updatedJob: IJob = {
      ...job!,
      completedOn: null,
      completedBy: null,
    }

    update.mutate(updatedJob, saveHandlers);
  }

  const handlePrint = async (part: IPart | null) => {
    if (!part) return;
    part.vehicle = job?.lead?.vehicle ?? null;

    const html = await generatePartLabel(part);
    printHtml(html);
  };

  const handlePrintVehicleLabel = async () => {
    const vehicle = job?.lead?.vehicle;
    if (!vehicle) {
      addNotification({ variant: "warning", primaryText: t("No vehicle on this job to label") });
      return;
    }

    const html = await generateVehicleLabel(vehicle);
    printHtml(html);
  };

  const handleMultiPrint = async () => {
    var parts = new Array();

    job?.jobPartCategories.forEach(jobPartCategories => {
      parts.push(jobPartCategories.partData);
    });

    await printParts(parts);
  };

  // Print labels for just this station's assembly (Station Manager).
  const handleStationPrint = async () => {
    const assembly = getAssemblyById(restrictToAssemblyId);
    const categoryIds = new Set(
      (assembly?.partAssemblyPartCategories ?? [])
        .map((c) => c.partCategoryId)
        .filter((id): id is string => !!id)
    );

    const parts = visibleJobPartCategories
      .filter((jpc) => jpc.partCategoryId && categoryIds.has(jpc.partCategoryId))
      .map((jpc) => jpc.partData)
      .filter((p): p is IPart => !!p);

    if (parts.length === 0) {
      addNotification({ variant: "warning", primaryText: t("No parts to print for this station") });
      return;
    }

    await printParts(parts);
  };

  // This will exclude any parts that are not yet removed, or are damanged
  const handleRemovedMultiPrint = async () => {
    var parts = new Array();

    job?.jobPartCategories.forEach(jobPartCategories => {
      if (jobPartCategories.partData?.partStatus === PartStatus.Dismantled) parts.push(jobPartCategories.partData);
    });

    if (parts.length === 0) {
      console.error("No removed parts to print labels for.");
      return;
    }

    await printParts(parts);
  }

  // Print labels for every part except those on a Recycling account. Parts with
  // no account are included.
  const handleAccountLabelsPrint = async () => {
    const accounts = flowTypeAccounts.data?.data ?? [];

    const parts = (job?.jobPartCategories ?? [])
      .map(jobPartCategories => jobPartCategories.partData)
      .filter((part): part is IPart => {
        if (!part) return false;
        if (!part.accountId) return true;
        const account = accounts.find(a => a.id === part.accountId);
        return account?.accountType !== AccountType.Recycling;
      });

    if (parts.length === 0) {
      addNotification({
        variant: "warning",
        primaryText: t("No account labels to print"),
      });
      return;
    }

    await printParts(parts);
  }

  // Print account labels (every part except those on a Recycling account)
  // scoped to the assembly currently in view: all assemblies on the normal job
  // page, or just this station's assembly in Station Manager.
  // When silent is true (auto-print after a scan starts the timer), skip the
  // empty-state toast so a station with nothing to label stays quiet.
  const handleStationLabelsPrint = async (options?: { silent?: boolean }) => {
    const accounts = flowTypeAccounts.data?.data ?? [];

    let scoped = visibleJobPartCategories;
    if (restrictToAssemblyId) {
      const assembly = getAssemblyById(restrictToAssemblyId);
      const categoryIds = new Set(
        (assembly?.partAssemblyPartCategories ?? [])
          .map((c) => c.partCategoryId)
          .filter((id): id is string => !!id)
      );
      scoped = scoped.filter((jpc) => jpc.partCategoryId && categoryIds.has(jpc.partCategoryId));
    }

    const parts = scoped
      .map((jpc) => jpc.partData)
      .filter((part): part is IPart => {
        if (!part) return false;
        if (!part.accountId) return true;
        const account = accounts.find((a) => a.id === part.accountId);
        return account?.accountType !== AccountType.Recycling;
      });

    if (parts.length === 0) {
      if (!options?.silent) {
        addNotification({
          variant: "warning",
          primaryText: t("No account labels to print"),
        });
      }
      return;
    }

    await printParts(parts);
  };

  // Fire Station Labels to the browser print dialog (default printer) whenever
  // a vehicle scan successfully starts an assembly timer. TME-only — same gate
  // as the manual Station Labels button.
  const autoPrintStationLabels = () => {
    if (!hasTmeFeature) return;
    void handleStationLabelsPrint({ silent: true });
  };

  const handleSelectedPartsPrint = async () => {
    if (selectedPartIds.size === 0) {
      addNotification({
        variant: "warning",
        primaryText: t("No Parts Selected"),
      });
      return;
    }

    const parts = job?.jobPartCategories
      .filter(jobPart => jobPart.partData?.id && selectedPartIds.has(jobPart.partData.id))
      .map(jobPart => jobPart.partData)
      .filter((part): part is IPart => part !== null) || [];

    if (parts.length === 0) {
      addNotification({
        variant: "warning",
        primaryText: t("No Valid Parts Selected"),
      });
      return;
    }

    await printParts(parts);
  }

  const dismantlingPlanHeader: DismantlingPlanHeader = {
    jobTitle: job?.title,
    vrm: job?.lead?.vehicle?.vrm,
    makeModel: job?.lead?.vehicle
      ? [job.lead.vehicle.combinedMake, job.lead.vehicle.combinedModel].filter(Boolean).join(" ")
      : null,
  };

  const handlePrintDismantlingPlan = () => {
    const parts = showDeleted
      ? (job?.jobPartCategories ?? [])
      : (job?.jobPartCategories ?? []).filter((jpc) => {
        if (!jpc.partData) return true;
        const part = jpc.partData as IPart & { deleted?: string | null };
        return !part.deleted;
      });

    const { groups, unmatched } = buildJobAssemblyGroups(assemblies.data?.data, parts);

    const planGroups: DismantlingPlanGroup[] = [
      ...groups.map((g) => ({ name: g.name, jobParts: g.jobParts })),
      ...(unmatched.length > 0 ? [{ name: t("Non-Assembly Parts"), jobParts: unmatched }] : []),
    ];

    if (planGroups.length === 0) {
      addNotification({ variant: "warning", primaryText: t("No parts to print") });
      return;
    }

    printDismantlingPlan(planGroups, dismantlingPlanHeader, flowTypeAccounts.data?.data ?? []);
  };

  const handleMarkSelectedAsRemoved = () => {
    if (selectedPartIds.size === 0) {
      addNotification({
        variant: "warning",
        primaryText: t("No Parts Selected"),
      });
      return;
    }

    const selectedParts = job?.jobPartCategories
      .filter(jobPart => jobPart.partData?.id && selectedPartIds.has(jobPart.partData.id))
      .map(jobPart => jobPart.partData)
      .filter((part): part is IPart => part !== null && !part.removedOn) || [];

    if (selectedParts.length === 0) {
      addNotification({
        variant: "warning",
        primaryText: t("No Valid Parts Selected"),
      });
      return;
    }

    // Set up pin modal to mark all selected parts as removed with a single pin entry
    requestPin({
      onPinSuccess: (pinUserId) => {
        // Mark all selected parts as removed
        selectedParts.forEach(part => {
          const updatedPart = {
            id: part.id,
            removedOn: new Date().toISOString(),
            removedBy: pinUserId,
            partStatus: PartStatus.Dismantled,
          };

          patchPart.mutate(updatedPart, {
            onSuccess: () => {
              partSaveHandlers.onSuccess();
            },
            onError: partSaveHandlers.onError,
          });
        });

        // Clear selection after marking
        setSelectedPartIds(new Set());
      },
      onPinFailed: () => {
      },
      pinLabel: selectedParts.length > 1
        ? t("Mark {{count}} Selected Parts as Removed", { count: selectedParts.length })
        : t("Mark {{count}} Selected Part as Removed", { count: selectedParts.length }),
    });
  }

  const togglePartSelection = (partId: string | null | undefined) => {
    if (!partId) return;

    setSelectedPartIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(partId)) {
        newSet.delete(partId);
      } else {
        newSet.add(partId);
      }
      return newSet;
    });
  }

  const handleDiagramClick = (jobPart: JobPartCategory) => {
    const cat = jobPart.partCategory;
    if (!cat) return;

    const matchedUcid = jobPart.partData?.matchedUcid;

    setDiagramCategory(cat);
    setDiagramPartDataId(jobPart.partData?.id ?? null);
    if (cat.ucidZone && matchedUcid) {
      setDiagramMode({ type: "view", zoneId: cat.ucidZone, ucid: matchedUcid });
    } else if (cat.ucidZone) {
      setDiagramMode({ type: "findZone", zoneId: cat.ucidZone });
    } else {
      setDiagramMode({ type: "findAll" });
    }
    setDiagramOpen(true);
  };

  const toggleAssemblySelection = (assemblyParts: JobPartCategory[]) => {
    const partIds = assemblyParts
      .map(jobPart => jobPart.partData?.id)
      .filter((id): id is string => id !== null && id !== undefined);

    if (partIds.length === 0) return;

    // Check if all parts in assembly are selected
    const allSelected = partIds.every(id => selectedPartIds.has(id));

    setSelectedPartIds(prev => {
      const newSet = new Set(prev);
      if (allSelected) {
        // Deselect all parts in assembly
        partIds.forEach(id => newSet.delete(id));
      } else {
        // Select all parts in assembly
        partIds.forEach(id => newSet.add(id));
      }
      return newSet;
    });
  }

  const printParts = async (parts: any[]) => {
    let htmlPages = [] as string[];

    // Use Promise.all to wait for all promises to resolve
    await Promise.all(parts.map(async (part) => {
      const html = await generatePartLabel(part);
      htmlPages.push(html);
    }));

    //Stitch together with a page break inbetween each
    const allHtml = htmlPages.join("<div style='page-break-after: always;'></div>");

    printHtml(allHtml);
  }

  // Job note text for a part-level report. The message is optional (scan-driven
  // damaged / missing reports carry none), so don't leave a dangling newline.
  const partNoteMessage = (heading: string, part: IPart | null | undefined, message: string) => {
    const header = `${heading} ${part?.title} - ${part?.tagNumber}`;
    return message.trim() ? `${header}\n${message}` : header;
  };

  const submitDamaged = (part: IPart | null | undefined, message: string) => {
    requestPin({
      onPinSuccess: (pinUserId) => {
        let body = {
          id: part?.id,
          isDamaged: !part?.isDamaged,
          partStatus: PartStatus.Damaged,
          damageReportedBy: pinUserId,
          damageMessage: message,
        };

        setIsDamagedLoading(true);

        markDamaged.mutate(body, {
          onSuccess: () => {
            setDamagedOpen(false);
            saveHandlers.onSuccess();
          },
          onError: () => {
            saveHandlers.onError();
          },
          onSettled: () => {
            setIsDamagedLoading(false);
          }
        })

        const newNote: INewNote = {
          message: partNoteMessage("Damage Reported For", part, message),
          resourceId: job?.id!,
          resourceType: ResourceType.Job,
          date: new Date().toISOString(),
        };

        create.mutate(newNote, {
          onSuccess: () => {
            saveHandlers.onSuccess();
            setTimeout(() => {
              queryClient.refetchQueries(["job", jobId]);
            }, 200);
          },
          onError: saveHandlers.onError
        });
      },
      onPinFailed: () => {
      },
      pinLabel: t("Mark Part Damaged"),
    });
  };

  const handleDamaged = (message: string) => submitDamaged(damaged, message);

  const submitMissing = (part: IPart | null | undefined, message: string) => {
    requestPin({
      onPinSuccess: (pinUserId) => {
        let body = {
          id: part?.id,
          isMissing: !part?.isMissing,
          partStatus: PartStatus.Missing,
          damageReportedBy: pinUserId,
          damageMessage: message,
        };

        setIsMissingLoading(true);

        markDamaged.mutate(body, {
          onSuccess: () => {
            setMissingOpen(false);
            saveHandlers.onSuccess();
          },
          onError: () => {
            saveHandlers.onError();
          },
          onSettled: () => {
            setIsMissingLoading(false);
          }
        })

        const newNote: INewNote = {
          message: partNoteMessage("Part Reported Missing For", part, message),
          resourceId: job?.id!,
          resourceType: ResourceType.Job,
          date: new Date().toISOString(),
        };

        create.mutate(newNote, {
          onSuccess: () => {
            saveHandlers.onSuccess();
            setTimeout(() => {
              queryClient.refetchQueries(["job", jobId]);
            }, 200);
          },
          onError: saveHandlers.onError
        });
      },
      onPinFailed: () => {
      },
      pinLabel: t("Mark Part Missing"),
    });
  };

  const handleMissing = (message: string) => submitMissing(missing, message);

  // The dismantler couldn't get to the part, so it stays on the car. Shares the
  // damage-report fields with damaged / missing and leaves a job note like them.
  const submitNotAccessible = (part: IPart | null | undefined) => {
    requestPin({
      onPinSuccess: (pinUserId) => {
        const body = {
          id: part?.id,
          isNotAccessible: !part?.isNotAccessible,
          partStatus: PartStatus["Not Accessible"],
          damageReportedBy: pinUserId,
          damageMessage: "",
        };

        markDamaged.mutate(body, {
          onSuccess: saveHandlers.onSuccess,
          onError: saveHandlers.onError,
        });

        const newNote: INewNote = {
          message: partNoteMessage("Part Reported Not Accessible For", part, ""),
          resourceId: job?.id!,
          resourceType: ResourceType.Job,
          date: new Date().toISOString(),
        };

        create.mutate(newNote, {
          onSuccess: () => {
            setTimeout(() => {
              queryClient.refetchQueries(["job", jobId]);
            }, 200);
          },
          onError: saveHandlers.onError,
        });
      },
      onPinFailed: () => {
      },
      pinLabel: t("Mark Part Not Accessible"),
    });
  };

  // "Add note" from the scan modal: a plain job note against the scanned part,
  // without changing the part's status.
  const handlePartNote = (message: string) => {
    const part = noteForPart;
    if (!part || !message.trim()) {
      setNoteForPart(null);
      return;
    }

    setIsNoteLoading(true);
    const newNote: INewNote = {
      message: partNoteMessage("Note For", part, message),
      resourceId: job?.id!,
      resourceType: ResourceType.Job,
      date: new Date().toISOString(),
    };

    create.mutate(newNote, {
      onSuccess: () => {
        saveHandlers.onSuccess();
        setNoteForPart(null);
        setTimeout(() => {
          queryClient.refetchQueries(["job", jobId]);
        }, 200);
      },
      onError: saveHandlers.onError,
      onSettled: () => setIsNoteLoading(false),
    });
  };

  const markAsRemoved = (part: IPart | null) => {
    if (!userId || !part) {
      partSaveHandlers.onError();
      return;
    }

    requestPin({
      onPinSuccess: (pinUserId) => {
        const updatedPart = {
          id: part.id,
          removedOn: new Date().toISOString(),
          removedBy: pinUserId,
          partStatus: PartStatus.Dismantled,
        };

        patchPart.mutate(updatedPart, {
          onSuccess: () => {
            partSaveHandlers.onSuccess();
          },
          onError: partSaveHandlers.onError,
        });
      },
      onPinFailed: () => {
      },
      pinLabel: t("Mark Part Removed"),
    });

  };

  const handleDeleteJob = () => {
    removeJob.mutate(jobId!, {
      onSuccess: () => {
        deleteHandlers.onSuccess();
        setDeleteOpen(false);
        //go back to jobs
        navigate("/jobs");
      },
      //Surface the reason the API gave rather than a bare "failed to delete job"
      onError: (err: any) =>
        addNotification({
          variant: "error",
          primaryText: t("Failed to delete job"),
          secondaryText: err?.message,
        }),
    });
  };

  const pages = [
    { to: "..", label: t("Jobs"), current: false },
    {
      to: jobId && job ? `/jobs/${jobId}` : "",
      label: jobId && job ? t("Job for {{title}}", { title: job.title.replace(/-[^-]*$/, "") }) : t("New Lead"),
      current: true,
    },
  ];

  const isStationMode = stationPicker !== undefined;

  if (isLoading) {
    if (!isStationMode) return <LoadingWheel />;
    // Keep the picker on screen while the job resolves so it doesn't blink out
    // between the scan and the parts appearing.
    return (
      <div className="space-y-4">
        <div className="rounded-lg bg-white p-4 shadow">{stationPicker}</div>
        <LoadingWheel />
      </div>
    );
  }

  const visibleJobPartCategories = showDeleted
    ? (job?.jobPartCategories || [])
    : (job?.jobPartCategories || []).filter(
      (jpc) => {
        if (!jpc.partData) return true;
        const part = jpc.partData as IPart & { deleted?: string | null };
        return !part.deleted;
      }
    );

  // Print options, shared by the toolbars above the overview and above the
  // parts list. In Station Manager the "all labels" option prints just this
  // station's assembly instead.
  const printMenuItems: ActionMenuItem[] = [
    {
      label: restrictToAssemblyId ? t("Station Part Labels") : t("All Part Labels"),
      onClick: () => (restrictToAssemblyId ? handleStationPrint() : handleMultiPrint()),
    },
    { label: t("Removed Part Labels"), onClick: () => handleRemovedMultiPrint() },
    { label: t("Vehicle Label"), onClick: () => handlePrintVehicleLabel() },
    { label: t("Account Labels"), onClick: () => handleAccountLabelsPrint(), hidden: !hasTmeFeature },
    { label: t("Dismantling Plan"), onClick: () => handlePrintDismantlingPlan() },
    {
      label: t("Selected Parts ({{count}})", { count: selectedPartIds.size }),
      onClick: () => handleSelectedPartsPrint(),
      disabled: selectedPartIds.size === 0,
    },
  ];

  // Job / navigation actions. Complete & Reopen stay as standalone buttons.
  const actionMenuItems: ActionMenuItem[] = [
    {
      label: t("Mark Selected as Removed ({{count}})", { count: selectedPartIds.size }),
      onClick: () => handleMarkSelectedAsRemoved(),
      disabled: selectedPartIds.size === 0,
    },
    {
      // Opens with anything already ticked on the page pre-selected.
      label: t("Allocate Parts to Bin"),
      onClick: () => openAllocateModal(null, true),
    },
    { label: t("Reset Job"), onClick: () => handleReset(), hidden: !(job?.startDate && job?.endDate) },
    {
      label: t("View Vehicle"),
      onClick: () => navigate(`/vehicles/${job?.lead?.vehicle?.id}`),
      hidden: !job?.lead?.vehicle?.id,
    },
    {
      label: t("View Lead"),
      onClick: () => navigate(`/leads/${job?.lead?.id}/parts`),
      hidden: !(hasLeads && job?.lead?.id),
    },
    // Stock:AdminOnlyChanges - deleting is admin-only, matching the API
    { label: t("Delete Job"), onClick: () => setDeleteOpen(true), danger: true, hidden: restricted },
  ];

  // Job toolbar. Right-aligned above the breadcrumb on the standalone page;
  // in Station Manager it sits under the station picker in the header.
  const toolbar = (
    <div className={classNames("flex flex-wrap items-center gap-x-4 gap-y-2", isStationMode ? "" : "justify-end")}>
      {/* Operators at a station don't need deleted parts; keep the toggle to the job page. */}
      {(!hasTmeFeature || !isStationMode) && (
        <div className="flex items-center gap-x-2">
          {!hasTmeFeature && <VehicleLocationDropdown vehicle={job?.lead?.vehicle!} classes="" />}
          {!isStationMode && (
            <label className="flex items-center gap-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.target.checked)}
                className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm font-medium">{t("Show Deleted")}</span>
            </label>
          )}
        </div>
      )}

      {hasTmeFeature && (
        <ButtonInput label={t("Station Labels")} isSubmit={false} onClick={() => handleStationLabelsPrint()} classes={""} />
      )}

      <ActionMenu label={t("Print")} items={printMenuItems} />

      <ActionMenu label={t("Actions")} items={actionMenuItems} />

      {/* Completing / reopening the whole job is a job-page decision, not a
          station one - the station finishes its own assembly timer instead. */}
      {!isStationMode && (
        job?.completedOn ? (
          <ButtonInput label={t("Reopen Job")} isSubmit={false} onClick={() => handleReopen()} classes={"!bg-green-600 hover:!bg-green-700"} />
        ) : (
          <ButtonInput label={t("Complete Job")} isSubmit={false} onClick={() => setCompleteOpen(true)} classes={"!bg-green-600 hover:!bg-green-700"} />
        )
      )}
    </div>
  );

  // Smaller in the station header so the three header cards line up.
  const vehicleQr = job?.lead?.vehicle?.id ? (
    <ScanQrCode
      value={`v|${job.lead.vehicle.id}|${job.lead.vehicle.vrm ?? "NULL"}`}
      alt={t("Vehicle QR code")}
      size={200}
      className={isStationMode ? "h-28 w-28" : "h-44 w-44"}
    />
  ) : null;

  return (
    <>
      {diagramCategory && job?.lead?.vehicle?.umc && (
        <PartDiagramModal
          open={diagramOpen}
          setOpen={setDiagramOpen}
          umc={job.lead.vehicle.umc}
          category={diagramCategory}
          mode={diagramMode}
          partDataId={diagramPartDataId}
        />
      )}
      <PinProtect open={pinModalOpen} setOpen={setPinModalOpen} pinState={pinState} useAuthUser={false} />
      <ScanPartActionModal
        open={!!scanConfirmPart}
        setOpen={(open) => { if (!open) setScanConfirmPart(null); }}
        part={scanConfirmPart}
        vehicleVrm={job?.lead?.vehicle?.vrm}
        vehicleTitle={[job?.lead?.vehicle?.combinedMake, job?.lead?.vehicle?.combinedModel].filter(Boolean).join(" ") || undefined}
        stationName={restrictToAssemblyId ? getAssemblyById(restrictToAssemblyId)?.name : undefined}
        onRemove={() => handleActionScan("removed")}
        onDamage={() => handleActionScan("damaged")}
        onMissing={() => handleActionScan("missing")}
        onNotAccessible={() => handleActionScan("notaccessible")}
        onNote={() => handleActionScan("note")}
      />
      <ReasonedModal
        open={!!noteForPart}
        setOpen={(open: boolean) => { if (!open) setNoteForPart(null); }}
        onConfirm={handlePartNote}
        isLoading={isNoteLoading}
        title={t("Add a note for {{title}}", { title: noteForPart?.title ?? t("this part") })}
        message={t("The note is saved against the job and shown with the job's notes. The part's status isn't changed.")}
        reasonLabel={t("Note:")}
        confirmButtonText={t("Save note")}
      />
      <ScanVehicleActionModal
        open={!!scanVehicleAssembly}
        setOpen={(open) => { if (!open) setScanVehicleAssembly(null); }}
        assemblyName={scanVehicleAssembly?.name ?? undefined}
        vehicleId={job?.lead?.vehicle?.id}
        vehicleVrm={job?.lead?.vehicle?.vrm}
        onPause={() => {
          const assembly = scanVehicleAssembly;
          if (!assembly) return;
          setScanVehicleAssembly(null);
          pauseTimerFromScan(assembly);
        }}
        onFinish={finishFromScanModal}
      />
      <AllocatePartsToBinModal
        open={allocateOpen}
        setOpen={(open) => {
          setAllocateOpen(open);
          if (!open) {
            setAllocateBinId(null);
            setAllocatePartIds(new Set());
          }
        }}
        bins={partBins.data?.data ?? []}
        binsLoading={partBins.isLoading}
        selectedBinId={allocateBinId}
        onSelectBin={setAllocateBinId}
        parts={allocatableParts}
        scopeName={restrictToAssemblyId ? getAssemblyById(restrictToAssemblyId)?.name : undefined}
        selectedPartIds={allocatePartIds}
        onTogglePart={toggleAllocatePart}
        onToggleAllParts={toggleAllAllocateParts}
        onAllocate={handleAllocateConfirm}
        isAllocating={bulkPatchPart.isLoading}
      />
      <ConfirmModal
        open={!!pendingSwitchVehicle}
        setOpen={(open: boolean) => { if (!open) setPendingSwitchVehicle(null); }}
        onConfirm={() => {
          const pending = pendingSwitchVehicle;
          setPendingSwitchVehicle(null);
          if (pending && onSwitchVehicle) onSwitchVehicle(pending.id, pending.vrm, false);
        }}
        isLoading={false}
        title={t("Different vehicle scanned")}
        message={t("You've scanned a different vehicle to this one{{vrm}} - would you like to open that one?\n\nScan it again to open it and start the timer.", { vrm: pendingSwitchVehicle?.vrm ? ` (${pendingSwitchVehicle.vrm})` : "" })}
        confirmButtonText={t("Open vehicle")}
      />
      <ReasonedModal open={damagedOpen} setOpen={setDamagedOpen} onConfirm={handleDamaged} isLoading={isDamagedLoading}
        title={t("Mark Part as Damaged?")}
        message={t("Are you sure you want to mark this part as damaged? \nThis will remove {{title}} from the job and not allow it to be listed.", { title: damaged?.title })}
        confirmButtonText={t("Confirm")}
      />
      <ReasonedModal open={missingOpen} setOpen={setMissingOpen} onConfirm={handleMissing} isLoading={isMissingLoading}
        title={t("Mark Part as Missing?")}
        message={t("Are you sure you want to mark this part as missing? \nThis will remove {{title}} from the job and not allow it to be listed.", { title: missing?.title })}
        confirmButtonText={t("Confirm")}
        reasonLabel={t("Why is it missing?")}
        reasonRequired
      />
      <ConfirmModal open={completeOpen} setOpen={setCompleteOpen} onConfirm={handleComplete} isLoading={isCompleteLoading}
        title={t("Complete Job")} message={t("Are you sure you want to mark this job as completed?")} confirmButtonText={t("Complete")}
      />
      <ConfirmModal open={deleteOpen} setOpen={setDeleteOpen} onConfirm={() => handleDeleteJob()} isLoading={false}
        title={t("Delete Job?")}
        message={t("Are you sure you want to delete this job? \nThis cannot be undone. Parts still awaiting removal are deleted with it - parts already removed, damaged or missing are left as they are.")}
        confirmButtonText={t("Delete")}
      />
      {isStationMode ? (
        // Station Manager header, in three: picker + toolbar on the left, job
        // notes in the middle, vehicle QR on the right, so everything the
        // operator needs is in view.
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="flex flex-col gap-4 rounded-lg bg-white p-4 shadow">
            {stationPicker}
            {toolbar}
          </div>

          {/* Below lg the notes and QR share a row (QR sized to content);
              at lg `contents` dissolves this wrapper so they become cells of
              the three-column grid alongside the picker. */}
          <div className={classNames("grid grid-cols-1 gap-4 lg:contents", vehicleQr ? "sm:grid-cols-[1fr_auto]" : "")}>
            {/* Card's white panel is a child of its container, so it needs h-full
                to stretch to the grid row and line up with the QR card. */}
            <Card
              bodyClassName="px-4 py-4"
              containerClassName={classNames("[&>div]:h-full", vehicleQr ? "" : "lg:col-span-2")}
            >
              <p className="text-sm font-medium text-gray-700">{t("Job Notes")}</p>
              <NotesOutput
                resourceType={ResourceType.Job}
                resourceId={job?.id!}
                shouldScroll={false}
                compact
              />
            </Card>

            {vehicleQr && (
              // Compact header: title and reg inline in small text so the card
              // is mostly QR code.
              <Card bodyClassName="flex flex-col items-center gap-3 px-4 py-4" containerClassName="[&>div]:h-full">
                <p className="flex w-full items-baseline gap-x-2 text-sm">
                  <span className="font-medium text-gray-700">{t("Vehicle QR")}</span>
                  {job?.lead?.vehicle?.vrm && (
                    <span className="font-semibold uppercase text-gray-500">{job.lead.vehicle.vrm}</span>
                  )}
                </p>
                {vehicleQr}
                <p className="text-center text-xs text-gray-500">
                  {t("Scan to open this vehicle")}
                </p>
              </Card>
            )}
          </div>
        </div>
      ) : (
        <>
          {toolbar}

          <Breadcrumbs pages={pages} />
        </>
      )}

      <div className="mt-4 space-y-4">

        {/* Notes + vehicle QR */}
        {!isStationMode && (
          <div className={`grid grid-cols-1 gap-4${vehicleQr ? " lg:grid-cols-3" : ""}`}>
            <Card
              title={t("Job Notes")}
              bodyClassName="px-2 pb-4"
              containerClassName={vehicleQr ? "lg:col-span-2" : undefined}
            >
              <NotesOutput
                resourceType={ResourceType.Job}
                resourceId={job?.id!}
                shouldScroll={false}
              />
            </Card>

            {vehicleQr && (
              <Card
                title={t("Vehicle QR")}
                subtitle={job?.lead?.vehicle?.vrm ?? undefined}
                bodyClassName="flex flex-col items-center justify-center gap-3 px-4 py-6"
              >
                {vehicleQr}
                <p className="text-center text-sm text-gray-500">
                  {t("Scan to open this vehicle")}
                </p>
              </Card>
            )}
          </div>
        )}

        {!hideTriage && job?.lead?.vehicle?.id && (
          hasTriage
            ? <Triage vehicleId={job.lead.vehicle.id} collapsible />
            : <Depollution vehicleId={job.lead.vehicle.id} collapsible />
        )}

        {!hideOverview && <JobOverviewCard job={job} jobPartCategories={visibleJobPartCategories} />}

        {!hideDismantlingTimer && (
          <JobDismantlingTimerCard jobId={jobId} collapsible={collapseDismantlingTimer} defaultOpen={!collapseDismantlingTimer} />
        )}

        {!isStationMode && (
          <div className="flex justify-end gap-x-4">
            {hasTmeFeature && (
              <ButtonInput label={t("Station Labels")} isSubmit={false} onClick={() => handleStationLabelsPrint()} classes={""} />
            )}

            <ActionMenu label={t("Print")} items={printMenuItems} />

            <ActionMenu label={t("Actions")} items={actionMenuItems} />
          </div>
        )}

        <Card bodyClassName="p-4" containerClassName="scroll-mt-4" labelId="parts">
          <div ref={partsSectionRef} className="space-y-4">
            <JobPartsByAssembly
              jobId={jobId}
              jobPartCategories={visibleJobPartCategories}
              markAsRemoved={markAsRemoved}
              setDamaged={setDamaged}
              setDamagedOpen={setDamagedOpen}
              setMissing={setMissing}
              setMissingOpen={setMissingOpen}
              markNotAccessible={submitNotAccessible}
              handlePrint={handlePrint}
              selectedPartIds={selectedPartIds}
              togglePartSelection={togglePartSelection}
              toggleAssemblySelection={toggleAssemblySelection}
              onDiagramClick={job?.lead?.vehicle?.umc ? handleDiagramClick : undefined}
              restrictToAssemblyId={restrictToAssemblyId}
              hideRecyclingParts={hideRecyclingParts}
              compact={compactParts}
              printHeader={dismantlingPlanHeader}
              onFinishRequest={hasTmeFeature ? confirmFinishWithOutstanding : undefined}
            />
          </div>
        </Card>
      </div>
    </>
  )
}
